import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, Phone, ShoppingCart, Check, ArrowRight, Info, Calculator, CheckCircle2, RefreshCw, X, Minus, Plus, ZoomIn, FileText, Download, Mail, Truck, Star, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { categories } from '../data/products';
import { QuoteModal } from './QuoteModal';
import { useLocale, getProductDescription } from '../utils/i18n';
import { SameDayBadge } from './SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems, getSmallLoadSurcharge } from '../utils/loadSplit';
import { SmallLoadBreakdown } from './SmallLoadBreakdown';
import { CartHelper } from './CartHelper';
import { MaterialCalculator } from './MaterialCalculator';
const TOP_SELLER_SLUGS = new Set(['5-8-minus', '1-25-minus', '5-8-clear', '1-5-drain-rock', '2-4-quarry-rock', '3-way-topsoil', 'medium-bark']);
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark') || name.includes('play chips')) return 0.45;
  if (name.includes('compost')) return 0.6;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase();
  return (
    // Fill dirt removed — heavy base material, ships 12 yd/load.
    // 50/50 Mix removed — heavy blend (sand + topsoil) ships 12 yd/load.
    n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips')
  );
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  useEffect(() => {
    setIdx(0);
    setIsPaused(false);
  }, [images]);
  // Auto-advance every 5 seconds
  useEffect(() => {
    if (!images || images.length <= 1 || isPaused || isZoomed) return;
    const timer = setInterval(() => {
      setIdx(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, [images, isPaused, isZoomed]);
  const activeImage = images && images.length > 0 ? images[idx] : '';
  return <>
      <div className="absolute inset-0 w-full h-full cursor-pointer group overflow-hidden" onClick={() => setIsZoomed(true)} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-1584">
        {!images || images.length <= 1 ? <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-1585" /> : images.map((src, i) => <img key={src} src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" style={{
        opacity: i === idx ? 1 : 0
      }} data-id="element-1586" />)}
      </div>

      {/* Fullscreen Zoom Overlay */}
      <AnimatePresence data-id="element-1587">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center touch-none" onClick={() => setIsZoomed(false)} data-id="element-1588">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white bg-black/50 p-2 rounded-full transition-colors z-[110]" onClick={e => {
          e.stopPropagation();
          setIsZoomed(false);
        }} aria-label="Close zoom" data-id="element-1589">
              <X className="w-6 h-6" data-id="element-1590" />
            </button>

            <motion.img initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} src={activeImage} alt={alt} className="max-w-full max-h-full object-contain" style={{
          touchAction: 'pan-x pan-y pinch-zoom'
        }} data-id="element-1591" />
          </motion.div>}
      </AnimatePresence>
    </>;
};
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-1592">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-[#e8ede4] dark:bg-emerald-500/[0.07] border border-[#d4ddcf] dark:border-emerald-500/20 rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5fb478] focus:border-transparent transition-all flex items-center justify-between font-medium hover:bg-[#dde5d6] dark:hover:bg-emerald-500/[0.12] ${value ? '' : 'text-stone-500'}`} data-id="element-1593">
        <span className="truncate" data-id="element-1594">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1595" />
      </button>

      <AnimatePresence data-id="element-1596">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full bottom-full mb-2 bg-white dark:bg-[#383838] border border-[#d4ddcf] dark:border-white/10 rounded-none shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-1597">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-1598">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french-drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
const calcMaterials = [{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$67'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$67'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$61'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$44'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$44'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$24'
}, {
  id: 'builders-sand',
  name: 'Builders Sand',
  density: 1.5,
  priceRange: '$73'
}, {
  id: 'gravel-borrow',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$48'
}, {
  id: '58-clear',
  name: '5/8" Crushed Clear',
  density: 1.35,
  priceRange: '$87'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: '125-clear',
  name: '1.25" Crushed Clear',
  density: 1.35,
  priceRange: '$76'
}, {
  id: '34-clear',
  name: '3/4" Crushed Clear',
  density: 1.35,
  priceRange: '$89'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$76'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$60'
}, {
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$48'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$57'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$66'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$53'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$53'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$53'
}, {
  id: 'play-chips',
  name: 'Play Chips',
  density: 0.45,
  priceRange: '$55'
}, {
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$61'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$84'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$93'
}, {
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$72'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$152'
}];
interface QuizOption {
  value: string;
  label: string;
  result?: {
    name: string;
    desc: string;
  };
}
interface QuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
  dependsOn?: {
    questionId: string;
    value: string;
  };
}
const quizQuestions: QuizQuestion[] = [{
  id: 'project',
  question: 'What type of project are you working on?',
  options: [{
    value: 'driveway',
    label: 'Driveway or Parking Area'
  }, {
    value: 'drainage',
    label: 'Drainage (French drain, retaining wall)'
  }, {
    value: 'landscape',
    label: 'Landscaping or Garden Beds'
  }, {
    value: 'walkway',
    label: 'Walkway or Patio Base'
  }, {
    value: 'erosion',
    label: 'Erosion Control or Slope Stabilization'
  }, {
    value: 'concrete',
    label: 'Concrete, Masonry, or Leveling'
  }, {
    value: 'fill',
    label: 'Grading, Fill, or Raising Low Spots'
  }]
}, {
  id: 'driveway_layer',
  dependsOn: {
    questionId: 'project',
    value: 'driveway'
  },
  question: 'Which layer are you building?',
  options: [{
    label: 'The deep foundation (Base layer)',
    value: 'base',
    result: {
      name: '1.25" Minus',
      desc: 'A coarser crushed rock that compacts into a rock-solid foundation. Often used as the first layer before topping with finer gravel.'
    }
  }, {
    label: 'The top driving surface (Top layer)',
    value: 'top',
    result: {
      name: '5/8" Minus',
      desc: 'Our most popular driveway gravel — packs down tight and creates a smooth, firm driving surface.'
    }
  }, {
    label: 'Budget-friendly option (Recycled material)',
    value: 'budget',
    result: {
      name: 'Recycled Concrete or Recycled Asphalt',
      desc: 'Sustainable, affordable alternatives that perform like virgin rock. Recycled Asphalt even re-binds over time for a semi-hard surface. Great for rural driveways and parking areas.'
    }
  }]
}, {
  id: 'drainage_type',
  dependsOn: {
    questionId: 'project',
    value: 'drainage'
  },
  question: 'What kind of drainage?',
  options: [{
    label: 'Standard French Drain',
    value: 'french',
    result: {
      name: '7/8" Drain Rock',
      desc: 'The Puget Sound standard for residential French drains. Washed clean so water flows freely through it.'
    }
  }, {
    label: 'Heavy-duty / Behind Retaining Wall',
    value: 'heavy',
    result: {
      name: '1.5" Drain Rock or 1.25" Crushed Clear',
      desc: 'Larger stone with bigger voids for maximum water flow in heavy-duty drainage and retaining wall backfill.'
    }
  }]
}, {
  id: 'landscape_type',
  dependsOn: {
    questionId: 'project',
    value: 'landscape'
  },
  question: 'What are you trying to achieve?',
  options: [{
    label: 'Planting grass or new plants',
    value: 'plants',
    result: {
      name: '3-Way Topsoil',
      desc: 'A premium blend of topsoil, compost, and sand — everything your plants need to thrive from day one.'
    }
  }, {
    label: 'Amending heavy clay soil',
    value: 'amend',
    result: {
      name: '50/50 Mix',
      desc: 'Half topsoil, half compost. Packed with organic matter to break up clay and boost soil health.'
    }
  }, {
    label: 'Pure compost for enriching soil',
    value: 'compost',
    result: {
      name: 'Compost',
      desc: 'Rich, fully aged organic compost packed with beneficial microbes and nutrients. Top-dress lawns, enrich raised beds, or boost vegetable garden health.'
    }
  }, {
    label: 'Weed control and moisture retention',
    value: 'weeds',
    result: {
      name: 'Fine, Medium, or Coarse Bark',
      desc: 'Natural PNW bark mulch to protect beds, suppress weeds, and retain moisture. Fine Bark for a polished look, Medium for all-around use, Coarse for maximum longevity.'
    }
  }, {
    label: 'Decorative ground cover or dry creek bed',
    value: 'decorative',
    result: {
      name: 'Streambed Cobble, River Rock, or Lava Rock',
      desc: 'Streambed Cobble for a natural creek bed look, River Rock for smooth organic accents, or Lava Rock for bold reddish-brown color and lightweight coverage. All zero-maintenance.'
    }
  }, {
    label: 'Decorative walking path',
    value: 'path',
    result: {
      name: 'Pea Gravel',
      desc: 'Small, smooth rounded stones for a beautiful, crunching pathway that drains well.'
    }
  }, {
    label: 'Playground or play area surface',
    value: 'playground',
    result: {
      name: 'Play Chips',
      desc: 'Natural cedar wood chips screened for playground use. Soft, cushioning, and naturally insect-resistant. Meets safety standards for fall protection when installed at proper depth.'
    }
  }]
}, {
  id: 'walkway_type',
  dependsOn: {
    questionId: 'project',
    value: 'walkway'
  },
  question: 'What is the surface going to be?',
  options: [{
    label: 'Pavers or Flagstone (Need a base)',
    value: 'pavers',
    result: {
      name: '5/8" Minus + Builders Sand',
      desc: 'Use 5/8" Minus as a compacted sub-base, then a layer of Builders Sand to level and set your pavers.'
    }
  }, {
    label: 'Just gravel (Walking on it directly)',
    value: 'gravel',
    result: {
      name: 'Pea Gravel or 5/8" Minus',
      desc: 'Pea gravel for a loose decorative feel, or 5/8" Minus for a firm, packed path.'
    }
  }]
}, {
  id: 'erosion_type',
  dependsOn: {
    questionId: 'project',
    value: 'erosion'
  },
  question: 'What does the area look like?',
  options: [{
    label: 'Steep slope or embankment',
    value: 'steep',
    result: {
      name: '2-4" Quarry Spalls or Large Rocks',
      desc: 'Quarry Spalls (2-4") lock in place on slopes for standard rip-rap. For extreme erosion or shoreline protection, Large Rocks (1-4 man) provide maximum mass and stability — nothing moves them.'
    }
  }, {
    label: 'Gentle slope or ditch lining',
    value: 'gentle',
    result: {
      name: '1.25" Crushed Clear, Streambed Cobble, or 2-4" Quarry Spalls',
      desc: 'For gentle slopes, 1.25" Crushed Clear provides drainage and stability. Streambed Cobble offers a natural, decorative look while controlling erosion. For steeper ditches, step up to 2-4" Quarry Spalls.'
    }
  }, {
    label: 'Severe erosion or shoreline armoring',
    value: 'severe',
    result: {
      name: 'Large Rocks (1-4 Man)',
      desc: 'Massive quarry rocks ranging from 100 lbs to 2,000+ lbs. The ultimate solution for shoreline protection, steep bank armoring, and heavy rip-rap where nothing else will hold.'
    }
  }]
}, {
  id: 'concrete_type',
  dependsOn: {
    questionId: 'project',
    value: 'concrete'
  },
  question: 'What are you doing?',
  options: [{
    label: 'Mixing concrete or mortar',
    value: 'mixing',
    result: {
      name: 'Builders Sand',
      desc: 'Coarse, washed sand with consistent grain size — essential for mixing concrete, mortar, and stucco.'
    }
  }, {
    label: 'Leveling under pavers, a pool, or a shed',
    value: 'leveling',
    result: {
      name: 'Builders Sand',
      desc: 'Clean, coarse sand that spreads and levels easily. Perfect as a setting bed under pavers, pools, and structures.'
    }
  }]
}, {
  id: 'fill_type',
  dependsOn: {
    questionId: 'project',
    value: 'fill'
  },
  question: 'What do you need to accomplish?',
  options: [{
    label: "Raise grade or fill low spots (won't be planting)",
    value: 'grade',
    result: {
      name: 'Fill Dirt',
      desc: 'Our most affordable bulk material. Clean subsoil for raising grades, backfilling, and establishing drainage slopes.'
    }
  }, {
    label: 'Fill and then plant on top',
    value: 'plant',
    result: {
      name: 'Fill Dirt + 3-Way Topsoil',
      desc: 'Use Fill Dirt to build up the area, then top with 4-6 inches of 3-Way Topsoil for a plantable surface.'
    }
  }, {
    label: 'Affordable bulk fill for subgrade or earthwork',
    value: 'bulk',
    result: {
      name: 'Gravel Borrow',
      desc: 'A naturally occurring pit-run gravel blend that compacts well. Meets WSDOT Type 17 specs — great for building up subgrade, backfill, and general earthwork at a lower cost than crushed rock.'
    }
  }]
}];
export function Services() {
  const [activeTab, setActiveTab] = useState<'materials' | 'calculator' | 'quiz'>('materials');
  const {
    addToCart,
    openCart,
    isCartOpen
  } = useCart();
  const {
    locale
  } = useLocale();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [productTabs, setProductTabs] = useState<Record<string, 'overview' | 'specs' | 'docs'>>({});
  const getProductTab = (slug: string) => productTabs[slug] || 'overview';
  const setProductTab = (slug: string, tab: 'overview' | 'specs' | 'docs') => setProductTabs(prev => ({
    ...prev,
    [slug]: tab
  }));
  // Add to Cart State
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(12);
  // 5+ loads custom-quote warning state for the Multiple stepper.
  // Mirrors the Hero / Product behavior: + at 4 loads flips the readout
  // window into a clickable amber warning that opens the QuoteModal.
  // Tracked separately from selectedQuantity so the warning doesn't
  // commit a misleading "5 loads" yardage to the order.
  const [showQuoteWarning, setShowQuoteWarning] = useState(false);
  // Free-text yardage input — always active. Max 2 digits, clamped 1–96
  // on blur/Enter. +/− pads keep this in sync via the useEffect below.
  const [qtyDraft, setQtyDraft] = useState('12');
  const qtyInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (document.activeElement !== qtyInputRef.current) {
      setQtyDraft(String(selectedQuantity || 12));
    }
  }, [selectedQuantity]);
  const [showQuantityDropdown, setShowQuantityDropdown] = useState(false);
  const [addedFlash, setAddedFlash] = useState<string | null>(null);
  // Auto-dismiss the "Added to Truck!" confirmation card after 10s and
  // revert to the default Add-to-Truck state. Customer can still tap
  // Continue Shopping / View Cart at any point during the window.
  useEffect(() => {
    if (!addedFlash) return;
    const t = setTimeout(() => setAddedFlash(null), 10000);
    return () => clearTimeout(t);
  }, [addedFlash]);
  const [showAllCatalog, setShowAllCatalog] = useState(false);
  const [categoryGridView, setCategoryGridView] = useState<string | null>(null);
  const [categoryOverview, setCategoryOverview] = useState<string | null>(null);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [swipeHintDismissed, setSwipeHintDismissed] = useState(false);
  const [calcMaterial, setCalcMaterial] = useState('crushed-rock');
  // Counts how many times the customer has changed which product they're
  // viewing within the materials hub during this session. Used as the
  // intent signal for the "Swipe to browse products" hint — we only show
  // it once they've actually moved past the first product, which proves
  // they're shopping (vs. just landed on the page).
  const [productsViewedCount, setProductsViewedCount] = useState(0);
  // Inline Calculator State (full drawer-style calc inside each product card)
  const [showInlineCalc, setShowInlineCalc] = useState(false);
  const [calcShape, setCalcShape] = useState<'rectangle' | 'circle' | 'triangle' | 'irregular'>('rectangle');
  const [calcUnit, setCalcUnit] = useState<'ft' | 'm'>('ft');
  const [calcDepthUnit, setCalcDepthUnit] = useState<'in' | 'cm'>('in');
  const [inlineCalcLength, setInlineCalcLength] = useState('');
  const [inlineCalcWidth, setInlineCalcWidth] = useState('');
  const [inlineCalcDiameter, setInlineCalcDiameter] = useState('');
  const [inlineCalcBase, setInlineCalcBase] = useState('');
  const [inlineCalcHeight, setInlineCalcHeight] = useState('');
  const [inlineCalcDepth, setInlineCalcDepth] = useState('3');
  // Note: the effect that increments `productsViewedCount` based on
  // `carouselIndex` lives further down — it can't run here because
  // `carouselIndex` is declared later in the function body.
  // Swipe hint: shown the moment the customer reaches their SECOND product
  // in the same session — that's the earliest reliable signal that they're
  // actively browsing and would benefit from learning the swipe gesture.
  // Hidden during checkout (cart open) and on the category overview.
  useEffect(() => {
    if (productsViewedCount >= 2 && expandedCategory && !categoryOverview && !swipeHintDismissed && !isCartOpen) {
      setShowSwipeHint(true);
      // Auto-dismiss after 5 seconds so the hint never blocks the screen.
      const dismissTimer = setTimeout(() => setShowSwipeHint(false), 5000);
      return () => clearTimeout(dismissTimer);
    }
    setShowSwipeHint(false);
  }, [productsViewedCount, expandedCategory, categoryOverview, swipeHintDismissed, isCartOpen]);
  const handleAddToCartClick = (productName: string, categoryName: string) => {
    setAddingToCart(productName);
    // Light materials (bark, compost, topsoil, play chips) ride in 15 yd
    // truckloads and customers typically order a full load, so default
    // the qty selector to 15. Heavy materials stay at 12.
    setSelectedQuantity(isLightMaterial(productName) ? 15 : 12);
  };
  const handleConfirmAdd = (categoryName: string, productName: string, price: number) => {
    const isLight = isLightMaterial(productName);
    const maxQuantity = isLight ? 60 : 48;
    if (selectedQuantity > maxQuantity) {
      setIsQuoteModalOpen(true);
      return;
    }
    // Shared splitter enforces the 5yd-per-load minimum on a partial last
    // load (e.g. 16yd non-light → 12 + 5, never 12 + 4). Same rule the
    // estimator and cart already use.
    const lineItems = buildDeliveryLineItems(selectedQuantity, productName);
    lineItems.forEach(li => {
      addToCart({
        category: categoryName,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: price,
        requestedQuantity: li.requestedQuantity
      });
    });
    setAddingToCart(null);
    setAddedFlash(productName);
  };
  // Calculator State
  const [projectType, setProjectType] = useState(projectTypes[0].id);
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState(projectTypes[0].defaultDepth.toString());
  const [material, setMaterial] = useState(calcMaterials[0].id);
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  useEffect(() => {
    const selectedProject = projectTypes.find(p => p.id === projectType);
    if (selectedProject) {
      setDepth(selectedProject.defaultDepth.toString());
    }
  }, [projectType]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const l = parseFloat(length);
    const w = parseFloat(width);
    const d = parseFloat(depth);
    if (isNaN(l) || isNaN(w) || isNaN(d)) return;
    const depthInFeet = d / 12;
    const cubicFeet = l * w * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  const handleCalcAddToCart = () => {
    if (!calcResults) return;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    if (!selectedMaterial) return;
    // Find matching product in categories
    let matchedProduct: any = null;
    let matchedCategory: string = '';
    for (const cat of categories) {
      const prod = cat.products.find(p => p.name === selectedMaterial.name);
      if (prod) {
        matchedProduct = prod;
        matchedCategory = cat.name;
        break;
      }
    }
    if (matchedProduct) {
      const roundedYards = Math.ceil(calcResults.yards);
      // Split the calculated yardage into delivery line items so the cart
      // shows a clear breakdown and the 5yd-per-load minimum is enforced
      // on the partial last load (e.g. 16yd → 12 + 5).
      const lineItems = buildDeliveryLineItems(roundedYards, matchedProduct.name);
      lineItems.forEach(li => {
        addToCart({
          category: matchedCategory,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: matchedProduct.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      // Confirmation persists until the customer chooses Continue
      // Shopping or View Cart from the post-add CTAs. The cart drawer
      // no longer auto-opens — opening it immediately would hide the
      // CTAs the customer just earned the right to see.
      setAddedFlash(matchedProduct.name);
    } else {
      setIsQuoteModalOpen(true);
    }
  };
  // Quiz State
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<{
    name: string;
    desc: string;
  } | null>(null);
  const currentQuizQuestion = quizQuestions.find(q => {
    if (!q.dependsOn) return !quizAnswers[q.id];
    return quizAnswers[q.dependsOn.questionId] === q.dependsOn.value && !quizAnswers[q.id];
  });
  const handleQuizAnswer = (questionId: string, value: string, result?: {
    name: string;
    desc: string;
  }) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (result) {
      setQuizResult(result);
    }
  };
  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
  };
  // Hand the quiz recommendation to the calculator
  const handleCalculateFromQuiz = () => {
    if (!quizResult) return;
    let matchedId = 'crushed-rock';
    const exact = calcMaterials.find(m => m.name === quizResult.name);
    if (exact) matchedId = exact.id;else {
      const sorted = [...calcMaterials].sort((a, b) => b.name.length - a.name.length);
      for (const m of sorted) {
        if (quizResult.name.toLowerCase().includes(m.name.toLowerCase())) {
          matchedId = m.id;
          break;
        }
      }
    }
    setCalcMaterial(matchedId);
    setActiveTab('calculator');
  };
  const tabs = [{
    id: 'materials' as const,
    label: 'Our Materials'
  }, {
    id: 'calculator' as const,
    label: 'Calculator'
  }, {
    id: 'quiz' as const,
    label: 'Help Me Choose'
  }];
  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);
  // Bump the "products viewed" counter every time the carousel moves to a
  // different product within an expanded category. Ignored when the user is
  // in the category overview (no product is focused there). Counter survives
  // the session but resets on full page reload. Lives down here because it
  // depends on `carouselIndex`, which is declared just above.
  useEffect(() => {
    if (expandedCategory && !categoryOverview) {
      setProductsViewedCount(n => n + 1);
    }
  }, [carouselIndex, expandedCategory, categoryOverview]);
  // Reset quantity when carousel index or expanded category changes
  useEffect(() => {
    if (expandedCategory) {
      const category = categories.find(c => c.name === expandedCategory);
      if (category) {
        const product = category.products[carouselIndex];
        if (product) {
          // Default qty matches the truckload size: 15 for light
          // materials (bark/compost/topsoil/play chips), 12 for the
          // rest. Without this the selector lands on 12 even for a
          // light material that ships as a 15-yd full load.
          setSelectedQuantity(isLightMaterial(product.name) ? 15 : 12);
          setAddingToCart(null);
          setShowInlineCalc(false);
          setCalcShape('rectangle');
          setInlineCalcLength('');
          setInlineCalcWidth('');
          setInlineCalcDiameter('');
          setInlineCalcBase('');
          setInlineCalcHeight('');
        }
      }
    }
  }, [carouselIndex, expandedCategory]);
  // Auto-scroll the chip carousel so the active product chip is always visible.
  // Manual scrollLeft on the tab scroller ONLY — scrollIntoView ripples up and
  // also scrolls the document horizontally because the card uses -mx-4 edge-to-edge
  // on mobile (which creates a sliver of horizontal overflow on the page).
  useEffect(() => {
    if (!expandedCategory) return;
    if (categoryGridView === expandedCategory) return;
    if (categoryOverview === expandedCategory) return;
    const timer = setTimeout(() => {
      const scroller = document.querySelector<HTMLElement>(`[data-carousel-category="${expandedCategory}"]`);
      if (!scroller) return;
      const activeChip = scroller.querySelector<HTMLElement>(`[data-chip-index="${carouselIndex}"]`);
      if (!activeChip) return;
      const target = activeChip.offsetLeft - scroller.clientWidth / 2 + activeChip.clientWidth / 2;
      scroller.scrollTo({
        left: Math.max(0, target),
        behavior: 'smooth'
      });
    }, 50);
    return () => clearTimeout(timer);
  }, [carouselIndex, expandedCategory, categoryGridView, categoryOverview]);
  // Handle navigation from Navbar shop dropdown
  useEffect(() => {
    const handleMaterialsNav = () => {
      const params = new URLSearchParams(window.location.search);
      const cat = params.get('cat');
      const prod = params.get('prod');
      if (cat) {
        setExpandedCategory(cat);
        setCarouselIndex(prod ? parseInt(prod, 10) : 0);
        setActiveTab('materials');
        // Clear query params so a refresh doesn't re-open the card
        window.history.replaceState({}, '', window.location.pathname);
        const scrollToCategory = () => {
          const catEl = document.getElementById(`cat-${cat}`);
          const target = catEl || document.getElementById('materials');
          if (target) {
            const navHeight = document.querySelector('nav')?.offsetHeight || 80;
            const top = target.getBoundingClientRect().top + window.scrollY - navHeight + 10;
            window.scrollTo({
              top,
              behavior: 'smooth'
            });
          }
          // Scroll selected product tab to the left edge of the carousel
          const tabContainer = catEl?.querySelector('.overflow-x-auto');
          if (tabContainer) {
            const prodIdx = prod ? parseInt(prod, 10) : 0;
            const buttons = tabContainer.querySelectorAll('button');
            if (buttons[prodIdx]) {
              const btn = buttons[prodIdx] as HTMLElement;
              const scrollOffset = btn.offsetLeft - 4;
              tabContainer.scrollTo({
                left: scrollOffset,
                behavior: 'smooth'
              });
            }
          }
        };
        // First scroll after expansion renders, second after animation settles
        setTimeout(scrollToCategory, 500);
        setTimeout(scrollToCategory, 900);
      }
    };
    // Check on mount (for cross-page navigation)
    handleMaterialsNav();
    // Listen for same-page navigation
    window.addEventListener('materialsNav', handleMaterialsNav);
    const handleCollapse = () => {
      setExpandedCategory(null);
    };
    const handleReset = () => {
      setExpandedCategory(null);
      setActiveTab('materials');
    };
    window.addEventListener('materialsCollapse', handleCollapse);
    window.addEventListener('materialsReset', handleReset);
    return () => {
      window.removeEventListener('materialsNav', handleMaterialsNav);
      window.removeEventListener('materialsCollapse', handleCollapse);
      window.removeEventListener('materialsReset', handleReset);
    };
  }, []);
  const handlePrev = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (!category) return;
    setCarouselIndex(prev => prev === 0 ? category.products.length - 1 : prev - 1);
    // First interaction proves the user knows how to swipe — retire
    // the hint immediately so it doesn't keep nagging.
    setShowSwipeHint(false);
    setSwipeHintDismissed(true);
  };
  const handleNext = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (!category) return;
    setCarouselIndex(prev => prev === category.products.length - 1 ? 0 : prev + 1);
    setShowSwipeHint(false);
    setSwipeHintDismissed(true);
  };
  const setDotIndex = (index: number) => {
    setCarouselIndex(index);
    setShowSwipeHint(false);
    setSwipeHintDismissed(true);
  };
  return <>
      <section className="relative py-24 dark:bg-[#242322] transition-colors duration-300 overflow-hidden border-t border-transparent dark:border-[#404040] bg-[#fcfcfc]" id="materials" data-id="element-1599">
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#e8e0d2]/20 dark:from-[#3a3633]/20 to-transparent pointer-events-none" data-id="element-1600" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1601">
          {/* Header */}
          <div className="mb-20" data-id="element-1602">
            <motion.div initial={{
            width: 0,
            opacity: 0
          }} whileInView={{
            width: 64,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            width: {
              duration: 1.2,
              ease: [0.22, 1, 0.36, 1],
              delay: 1
            },
            opacity: {
              duration: 0.4,
              delay: 1
            }
          }} className="h-1 bg-brand-orange rounded-full mb-8" data-id="element-1603" />

            <div className="flex flex-col md:flex-row md:items-baseline gap-4 md:gap-8 mb-6" data-id="element-1604">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.1
            }} className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold text-brand-charcoal dark:text-white tracking-tight leading-[1.1]" data-id="element-1605">
                Materials <span className="text-brand-orange" data-id="element-1606">Hub</span>
              </motion.h2>

              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.15
            }} className="inline-block text-xs font-bold tracking-[0.2em] uppercase self-center text-[#17A348]" data-id="element-1607">
                — Explore Our Catalog
              </motion.span>
            </div>

            <motion.p initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.25
          }} className="text-brand-charcoal dark:text-white text-lg font-medium font-sub leading-relaxed max-w-lg tracking-wide" data-id="element-1608">
              Browse our catalog, estimate your needs, or find the right
              material for your next project.
            </motion.p>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]" data-id="element-1609">
            <AnimatePresence mode="wait" data-id="element-1610">
              {/* MATERIALS TAB */}
              {activeTab === 'materials' && <motion.div key="materials" initial={{
              opacity: 0,
              y: 20,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -20,
              scale: 0.98
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25
            }} className="space-y-0 divide-y divide-stone-200 dark:divide-[#4a4a4a]" data-id="element-1611">
                  {categories.map(category => {
                const isExpanded = expandedCategory === category.name;
                if (isExpanded) {
                  // Single-product carousel UI replacing the product grid
                  const product = category.products[carouselIndex] || null;
                  return <div key={category.name} id={`cat-${category.name}`} className="overflow-hidden scroll-mt-[135px]" data-id="element-1612">
                          <button onClick={() => {
                      if (isExpanded) {
                        setExpandedCategory(null);
                        setCategoryOverview(null);
                      } else {
                        setExpandedCategory(category.name);
                        setCategoryOverview(category.name);
                        setCarouselIndex(0);
                      }
                    }} className="w-full flex items-center justify-between py-3 sm:py-4 text-left focus:outline-none group" data-id="element-1613">
                            <div data-id="element-1614">
                              <h3 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors duration-200 ${isExpanded ? 'text-brand-orange' : 'text-brand-charcoal dark:text-white/80 group-hover:text-brand-orange'}`} data-id="element-1615">
                                {category.name}
                              </h3>
                              <span className="text-[11px] font-normal text-stone-400 dark:text-white/40 tracking-wide" data-id="element-1616">
                                {category.name === 'Soil, Mulch & Org.' ? '15' : '12'}{' '}
                                yards max load
                              </span>
                            </div>
                            <ChevronDown className={`w-7 h-7 shrink-0 ml-4 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-brand-orange' : 'text-brand-charcoal/30 dark:text-white/30 group-hover:text-brand-orange'}`} data-id="element-1617" />
                          </button>

                          <AnimatePresence mode="wait" data-id="element-1618">
                            {isExpanded && product && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.3,
                        ease: 'easeInOut'
                      }} data-id="element-1619">
                                <div className="pb-10" data-id="element-1620">
                                  {/* Category Overview - Large Stacked Image Cards */}
                                  {categoryOverview === category.name ? <motion.div key="category-overview" initial={{
                            opacity: 0,
                            y: 10
                          }} animate={{
                            opacity: 1,
                            y: 0
                          }} exit={{
                            opacity: 0,
                            y: -10
                          }} transition={{
                            duration: 0.25
                          }} className="space-y-1.5" data-id="element-1621">
                                      {category.products.map((p, idx) => <motion.button key={p.slug} initial={{
                              opacity: 0,
                              y: 20
                            }} animate={{
                              opacity: 1,
                              y: 0
                            }} transition={{
                              duration: 0.3,
                              delay: idx * 0.06
                            }} onClick={() => {
                              setCategoryOverview(null);
                              setCarouselIndex(idx);
                              setCategoryGridView(null);
                              setTimeout(() => {
                                const catEl = document.getElementById(`cat-${category.name}`);
                                if (catEl) {
                                  const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                  const top = catEl.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                                  window.scrollTo({
                                    top,
                                    behavior: 'smooth'
                                  });
                                }
                              }, 50);
                            }} className="w-full relative overflow-hidden rounded-xl group/overview block text-left" style={{
                              height: '200px'
                            }} data-id="element-1622">
                                          <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover/overview:scale-105 transition-transform duration-500" data-id="element-1623" />
                                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-1624" />
                                          {TOP_SELLER_SLUGS.has(p.slug) && <div className="absolute top-3 left-3 z-10" data-id="element-1625">
                                              <span className="inline-flex items-center gap-1.5 bg-amber-300 text-stone-900 text-xs sm:text-sm font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-md shadow-md animate-shimmer" data-id="element-1626">
                                                <Star className="w-3.5 h-3.5 fill-stone-900" data-id="element-1627" />
                                                Top Seller
                                              </span>
                                            </div>}
                                          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6" data-id="element-1628">
                                            <h4 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight mb-1" data-id="element-1629">
                                              {p.name}
                                            </h4>
                                            <p className="text-white/90 text-sm sm:text-base font-medium" data-id="element-1630">
                                              ${p.pricePerYard} / yard
                                            </p>
                                          </div>
                                        </motion.button>)}
                                    </motion.div> : <>
                                      {/* Category Items Carousel */}
                                      <div className="mb-6 flex items-center gap-2" data-id="element-1631">
                                        <div className="relative flex-1 min-w-0" data-id="element-1632">
                                          <div className="overflow-x-auto scrollbar-hide -mx-1 px-1" data-carousel-category={category.name} data-id="element-1633">
                                            <div className="flex items-center gap-1 whitespace-nowrap pb-1" style={{
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none'
                                  }} data-id="element-1634">
                                              {category.products.map((p, idx) => <button key={p.name} data-chip-index={idx} onClick={e => {
                                      setCategoryGridView(null);
                                      setCarouselIndex(idx);
                                      setAddingToCart(null);
                                      // Center the clicked chip in the tab scroller ONLY
                                      // (manual scrollLeft — never use scrollIntoView,
                                      // which can ripple up and scroll the page
                                      // horizontally because the parent card is
                                      // -mx-4 edge-to-edge on mobile).
                                      const btn = e.currentTarget;
                                      const scroller = btn.closest('[data-carousel-category]') as HTMLElement | null;
                                      if (scroller) {
                                        const target = btn.offsetLeft - scroller.clientWidth / 2 + btn.clientWidth / 2;
                                        scroller.scrollTo({
                                          left: Math.max(0, target),
                                          behavior: 'smooth'
                                        });
                                      }
                                    }} className={`relative shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${categoryGridView === category.name ? 'text-stone-600 dark:text-stone-200 hover:text-white' : idx === carouselIndex ? 'text-brand-orange bg-brand-orange/10' : 'text-stone-600 dark:text-white/80 hover:text-white hover:bg-white/5'}`} data-id="element-1635">
                                                    {p.name}
                                                    {idx === carouselIndex && categoryGridView !== category.name && <motion.div layoutId="activeProduct" className="absolute bottom-0 left-2 right-2 h-[2px] bg-brand-orange rounded-full" transition={{
                                        type: 'spring',
                                        stiffness: 400,
                                        damping: 25
                                      }} data-id="element-1636" />}
                                                  </button>)}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="shrink-0 border-l border-stone-300 dark:border-white/10 pl-3 ml-1" data-id="element-1637">
                                          <button onClick={() => setCategoryOverview(category.name)} className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all bg-stone-200 dark:bg-white/10 text-stone-800 dark:text-stone-300 hover:bg-brand-orange/15 hover:text-brand-orange" data-id="element-1638">
                                            ← Back
                                          </button>
                                        </div>
                                      </div>

                                      {/* Category Grid View */}
                                      {categoryGridView === category.name ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-4" data-id="element-1639">
                                          {category.products.map(p => <button key={p.slug} onClick={() => {
                                setCategoryGridView(null);
                                setCarouselIndex(category.products.findIndex(pr => pr.slug === p.slug));
                                setTimeout(() => {
                                  const catEl = document.getElementById(`cat-${category.name}`);
                                  if (catEl) {
                                    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                    const top = catEl.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                                    window.scrollTo({
                                      top,
                                      behavior: 'smooth'
                                    });
                                  }
                                }, 50);
                              }} className="bg-stone-200 dark:bg-[#282828] rounded-xl overflow-hidden border border-stone-400 dark:border-[#383838] hover:border-brand-orange/50 transition-all group/card text-left flex flex-col" data-id="element-1640">
                                              <div className="aspect-[4/3] overflow-hidden relative" data-id="element-1641">
                                                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" data-id="element-1642" />
                                                <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-bold" data-id="element-1643">
                                                  ${p.pricePerYard}/yd
                                                </div>
                                              </div>
                                              <div className="p-4" data-id="element-1644">
                                                <h4 className="font-bold text-brand-charcoal dark:text-white text-sm" data-id="element-1645">
                                                  {p.name}
                                                </h4>
                                                <p className="text-xs text-stone-600 dark:text-stone-200 mt-1 line-clamp-2" data-id="element-1646">
                                                  {p.description}
                                                </p>
                                              </div>
                                            </button>)}
                                        </div> : <div className="bg-[#f4f7f2] dark:bg-[#353433] md:rounded-2xl border border-[#d4ddcf] dark:border-[#4a4a4a] overflow-hidden flex flex-col md:flex-row shadow-[0_8px_28px_-8px_rgba(0,0,0,0.10)] dark:shadow-[0_0_36px_-6px_rgba(255,255,255,0.06)] -mx-4 sm:mx-0 relative touch-pan-y" onTouchStart={e => {
                              const touch = e.touches[0];
                              (e.currentTarget as any)._swipeStartX = touch.clientX;
                              (e.currentTarget as any)._swipeStartY = touch.clientY;
                            }} onTouchEnd={e => {
                              const startX = (e.currentTarget as any)._swipeStartX;
                              const startY = (e.currentTarget as any)._swipeStartY;
                              if (startX == null || startY == null) return;
                              const touch = e.changedTouches[0];
                              const deltaX = touch.clientX - startX;
                              const deltaY = touch.clientY - startY;
                              // Only trigger if horizontal swipe is dominant and significant
                              if (Math.abs(deltaX) > 100 && Math.abs(deltaX) > Math.abs(deltaY) * 2) {
                                if (deltaX < 0) {
                                  handleNext(category.name);
                                } else {
                                  handlePrev(category.name);
                                }
                                // Scroll the active product tab into view
                                // (manual scrollLeft on the tab scroller ONLY —
                                // scrollIntoView ripples up and shifts the whole
                                // card horizontally because of -mx-4 edge-to-edge).
                                setTimeout(() => {
                                  const catEl = document.getElementById(`cat-${category.name}`);
                                  if (catEl) {
                                    const scroller = catEl.querySelector('[data-carousel-category]') as HTMLElement | null;
                                    if (scroller) {
                                      const activeBtn = scroller.querySelector('.text-brand-orange') as HTMLElement | null;
                                      if (activeBtn) {
                                        const target = activeBtn.offsetLeft - scroller.clientWidth / 2 + activeBtn.clientWidth / 2;
                                        scroller.scrollTo({
                                          left: Math.max(0, target),
                                          behavior: 'smooth'
                                        });
                                      }
                                    }
                                  }
                                }, 50);
                              }
                              ;
                              (e.currentTarget as any)._swipeStartX = null;
                              (e.currentTarget as any)._swipeStartY = null;
                            }} data-id="element-1647">
                                          {/* Image — 2:3 ratio on mobile */}
                                          <div className="w-full md:w-1/2 aspect-[3/4] md:aspect-auto md:h-auto md:min-h-[400px] relative" data-id="element-1648">
                                            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-1649" />
                                            {TOP_SELLER_SLUGS.has(product.slug) && <div className="absolute top-3 left-3 z-20" data-id="element-1650">
                                                <span className="inline-flex items-center gap-1.5 bg-amber-300 text-stone-900 text-xs sm:text-sm font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-md shadow-md animate-shimmer" data-id="element-1651">
                                                  <Star className="w-3.5 h-3.5 fill-stone-900" data-id="element-1652" />
                                                  Top Seller
                                                </span>
                                              </div>}
                                            {/* Gradient fade for mobile with overlaid product name */}
                                            <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#f5f5f5] dark:from-[#353433] via-[#f5f5f5]/60 dark:via-[#353433]/60 to-transparent pointer-events-none md:hidden" data-id="element-1653" />
                                            <div className="absolute bottom-0 left-0 right-0 px-10 py-6 sm:p-6 z-10 md:hidden" data-id="element-1654">
                                              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider mb-1 block drop-shadow-md" data-id="element-1655">
                                                {category.name}
                                              </span>
                                              <h4 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white drop-shadow-md break-words" data-id="element-1656">
                                                {product.name}
                                              </h4>
                                            </div>
                                          </div>

                                          {/* Details */}
                                          <div className="w-full md:w-1/2 px-10 py-6 sm:p-8 md:p-10 flex flex-col relative z-10 -mt-6 md:mt-0" data-id="element-1657">
                                            <span className="hidden md:block text-xs font-bold text-brand-muted uppercase tracking-wider mb-2" data-id="element-1658">
                                              {category.name}
                                            </span>
                                            <h4 className="hidden md:block font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1659">
                                              {product.name}
                                            </h4>
                                            {/* Tabbed Info */}
                                            {(() => {
                                  const tab = getProductTab(product.slug);
                                  const tabs = [{
                                    id: 'overview' as const,
                                    label: 'Overview'
                                  }, {
                                    id: 'specs' as const,
                                    label: 'Specs'
                                  }, {
                                    id: 'docs' as const,
                                    label: 'Documents'
                                  }];
                                  return <div className="mb-5" data-id="element-1660">
                                                  <div className="flex border-b border-stone-200 dark:border-[#444] mb-4" data-id="element-1661">
                                                    {tabs.map(tb => <button key={tb.id} type="button" onClick={() => setProductTab(product.slug, tb.id)} className={`pb-2.5 px-3 text-sm font-bold transition-colors relative ${tab === tb.id ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-1662">
                                                        {tb.label}
                                                        {tab === tb.id && <motion.div layoutId={`tab-${product.slug}`} className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-1663" />}
                                                      </button>)}
                                                  </div>
                                                  <div className="min-h-[120px]" data-id="element-1664">
                                                    <AnimatePresence mode="wait" data-id="element-1665">
                                                      {tab === 'overview' && <motion.div key="overview" initial={{
                                          opacity: 0,
                                          y: 6
                                        }} animate={{
                                          opacity: 1,
                                          y: 0
                                        }} exit={{
                                          opacity: 0,
                                          y: -6
                                        }} transition={{
                                          duration: 0.18
                                        }} data-id="element-1666">
                                                          <p className="text-brand-charcoal/80 dark:text-white/90 text-base leading-relaxed mb-4" data-id="element-1667">
                                                            {getProductDescription(product, locale)}
                                                          </p>
                                                          {product.bestFor && product.bestFor.length > 0 && <div className="mb-4" data-id="element-1668">
                                                                <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1669">
                                                                  Best For
                                                                </p>
                                                                <div className="flex flex-wrap gap-1.5" data-id="element-1670">
                                                                  {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-1671">
                                                                        {tag}
                                                                      </span>)}
                                                                </div>
                                                              </div>}
                                                          {product.proTip && <div className="border border-[#d4ddcf] dark:border-emerald-500/20 rounded-sm p-4 flex items-start gap-3" data-id="element-1672">
                                                              <div className="bg-[#059F40]/15 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-1673">
                                                                <Check className="w-4 h-4 text-[#059F40] dark:text-[#22c95c]" data-id="element-1674" />
                                                              </div>
                                                              <div data-id="element-1675">
                                                                <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-1676">
                                                                  Pro Tip
                                                                </h4>
                                                                <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-1677">
                                                                  {product.proTip}
                                                                </p>
                                                              </div>
                                                            </div>}
                                                        </motion.div>}
                                                      {tab === 'specs' && <motion.div key="specs" initial={{
                                          opacity: 0,
                                          y: 6
                                        }} animate={{
                                          opacity: 1,
                                          y: 0
                                        }} exit={{
                                          opacity: 0,
                                          y: -6
                                        }} transition={{
                                          duration: 0.18
                                        }} data-id="element-1678">
                                                          {(product as any).specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1679">
                                                              {Object.entries((product as any).specs).map(([key, value]) => {
                                              const labels: Record<string, string> = {
                                                size: 'Size / Gradation',
                                                weightPerYard: 'Density (Weight/Yd)',
                                                compaction: 'Compaction',
                                                coverage: 'Est. Coverage'
                                              };
                                              return <div key={key} className="bg-stone-50 dark:bg-[#222] p-3 rounded-lg border border-stone-200 dark:border-[#333]" data-id="element-1680">
                                                                      <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1" data-id="element-1681">
                                                                        {labels[key] || key}
                                                                      </p>
                                                                      <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-1682">
                                                                        {String(value)}
                                                                      </p>
                                                                    </div>;
                                            })}
                                                            </div> : <p className="text-sm text-stone-700 dark:text-stone-300 py-4" data-id="element-1683">
                                                              No detailed
                                                              specifications
                                                              available. Call
                                                              (206) 619-3742 for
                                                              tech data.
                                                            </p>}
                                                        </motion.div>}
                                                      {tab === 'docs' && <motion.div key="docs" initial={{
                                          opacity: 0,
                                          y: 6
                                        }} animate={{
                                          opacity: 1,
                                          y: 0
                                        }} exit={{
                                          opacity: 0,
                                          y: -6
                                        }} transition={{
                                          duration: 0.18
                                        }} className="space-y-2" data-id="element-1684">
                                                          <button onClick={() => generateSpecSheet({
                                            product,
                                            categoryName: category.name
                                          })} className="w-full flex items-center gap-3 p-3 rounded-lg border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all text-left" data-id="element-1685">
                                                            <div className="w-9 h-9 rounded-md bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-1686">
                                                              <FileText className="w-4 h-4 text-brand-orange" data-id="element-1687" />
                                                            </div>
                                                            <div className="min-w-0 flex-1" data-id="element-1688">
                                                              <p className="text-sm font-bold text-stone-900 dark:text-white truncate" data-id="element-1689">
                                                                Product Spec
                                                                Sheet
                                                              </p>
                                                              <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-1690">
                                                                Specs, coverage,
                                                                pricing
                                                              </p>
                                                            </div>
                                                            <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-orange shrink-0" data-id="element-1691">
                                                              <Download className="w-3.5 h-3.5" data-id="element-1692" />
                                                            </span>
                                                          </button>
                                                          <div className="flex items-start gap-3 p-3 rounded-lg border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-1693">
                                                            <div className="w-9 h-9 rounded-md bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-1694">
                                                              <Mail className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" data-id="element-1695" />
                                                            </div>
                                                            <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-1696">
                                                              Need a gradation
                                                              curve, MSDS, or
                                                              compaction test?{' '}
                                                              <a href={`mailto:info@253gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-1697">
                                                                info@253gravel.com
                                                              </a>
                                                              .
                                                            </p>
                                                          </div>
                                                        </motion.div>}
                                                    </AnimatePresence>
                                                  </div>
                                                </div>;
                                })()}

                                            {category.name !== 'Specialty Rock' && product.pricePerYard > 0 ? <>
                                                <div className="mb-6 pb-6 border-b border-stone-200/50 dark:border-[#4a4a4a]/50" data-id="element-1698">
                                                  {(() => {
                                      const isBarkMulch = isLightMaterial(product.name);
                                      const qtyNum = selectedQuantity;
                                      const fullLoadYards = isBarkMulch ? 15 : 12;
                                      const minChargeYards = 3;
                                      const smallLoadThreshold = isBarkMulch ? 12 : 10;
                                      // Shared sub-5yd curve:
                                      // ≥threshold → 1×, 5..threshold
                                      // → 10%/yd step, 3..4 →
                                      // continues at 15%/yd step.
                                      const computeSurcharge = (y: number) => {
                                        if (y >= smallLoadThreshold) return 1;
                                        if (y >= 5) return 1 + (smallLoadThreshold - y) * 0.1;
                                        const at5 = 1 + (smallLoadThreshold - 5) * 0.1;
                                        return at5 + (5 - y) * 0.15;
                                      };
                                      // Helper: calculate total for a single-load quantity
                                      const calcLoadTotal = (yards: number) => {
                                        const billableYards = Math.max(yards, minChargeYards);
                                        const surcharge = computeSurcharge(billableYards);
                                        const price = Math.ceil(product.pricePerYard * surcharge);
                                        return billableYards * price;
                                      };
                                      // Helper: get effective per-yard price for display
                                      const calcEffectivePrice = (yards: number) => {
                                        const billableYards = Math.max(yards, minChargeYards);
                                        const surcharge = computeSurcharge(billableYards);
                                        return Math.ceil(product.pricePerYard * surcharge);
                                      };
                                      const isBeyondFullLoad = qtyNum > fullLoadYards;
                                      const isBelowMinCharge = qtyNum < minChargeYards;
                                      const displayYards = isBeyondFullLoad ? fullLoadYards : qtyNum;
                                      const effectivePrice = calcEffectivePrice(displayYards);
                                      const isSmallLoad = !isBeyondFullLoad && qtyNum < smallLoadThreshold;
                                      // Tiered bulk discount: light materials 5% at 15yds, others 5% at 24yds; 10% at 4 full loads
                                      const bulkDiscountPct = qtyNum >= fullLoadYards * 4 ? 0.1 : qtyNum >= (isBarkMulch ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
                                      const hasBulkDiscount = bulkDiscountPct > 0;
                                      // Past 4 full loads
                                      // dispatch sets
                                      // custom pricing
                                      // (hand loads, mixed
                                      // truck sizes), so
                                      // the calculator's
                                      // bulk-discount math
                                      // no longer matches
                                      // reality. Suppress
                                      // both the badge and
                                      // the savings line
                                      // past that
                                      // threshold.
                                      const isCustomQuoteZone = qtyNum > fullLoadYards * 4;
                                      // Build delivery loads
                                      const deliveryLoads: {
                                        yards: number;
                                        total: number;
                                      }[] = [];
                                      if (isBeyondFullLoad) {
                                        let remaining = qtyNum;
                                        while (remaining > 0) {
                                          const loadYards = Math.min(remaining, fullLoadYards);
                                          deliveryLoads.push({
                                            yards: loadYards,
                                            total: calcLoadTotal(loadYards)
                                          });
                                          remaining -= loadYards;
                                        }
                                      }
                                      let total = isBeyondFullLoad ? deliveryLoads.reduce((sum, l) => sum + l.total, 0) : calcLoadTotal(qtyNum);
                                      // Apply tiered bulk discount
                                      const preDiscountTotal = total;
                                      if (hasBulkDiscount) {
                                        total = Math.round(total * (1 - bulkDiscountPct));
                                        deliveryLoads.forEach((l, i) => {
                                          deliveryLoads[i] = {
                                            ...l,
                                            total: Math.round(l.total * (1 - bulkDiscountPct))
                                          };
                                        });
                                      }
                                      const extraYards = isBeyondFullLoad ? qtyNum - fullLoadYards : 0;
                                      const extraBillable = Math.max(extraYards, minChargeYards);
                                      const extraEffectivePrice = isBeyondFullLoad ? calcEffectivePrice(extraYards) : 0;
                                      // Materials breakdown is ALWAYS shown when
                                      // there's a quantity, so the wrapper renders
                                      // for every order with yards (not just
                                      // surcharge/discount edge cases).
                                      const hasMessages = qtyNum > 0 || hasBulkDiscount || isBelowMinCharge || isSmallLoad || isBeyondFullLoad;
                                      return <div className="p-4 sm:p-5" data-id="element-1699">
                                                        {/* Two-column Price /
                                                         Subtotal mirror —
                                                         matches the Product
                                                         page hero so the
                                                         "rate × quantity =
                                                         total" story reads
                                                         identically in both
                                                         the hub and the
                                                         dedicated product
                                                         page. */}
                                                        <div className="flex items-start justify-between gap-4" data-id="element-1700">
                                                          <div className="flex-1 min-w-0" data-id="element-1701">
                                                            {/* State-aware label above the big
                                                             price number. Matches the Product
                                                             page hero: green BULK DISCOUNT pill
                                                             when paying less, amber SMALL-LOAD
                                                             RATE pill when paying more, default
                                                             "Price" text otherwise. h-6 mb-1
                                                             container keeps the big numbers
                                                             baseline-aligned with the right
                                                             column regardless of which label
                                                             is showing. */}
                                                            <div className="h-6 mb-1 flex items-center" data-id="element-1702">
                                                              {!isCustomQuoteZone && hasBulkDiscount ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#059F40] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-1703">
                                                                  {Math.round(bulkDiscountPct * 100)}
                                                                  % Bulk
                                                                  Discount
                                                                </span> : !isCustomQuoteZone && effectivePrice > product.pricePerYard ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#EAB308] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-1704">
                                                                  Small-load
                                                                  rate
                                                                </span> : <p className="text-sm text-brand-muted dark:text-[#22c95c] font-medium" data-id="element-1705">
                                                                  Price
                                                                </p>}
                                                            </div>
                                                            {isCustomQuoteZone ? <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-1706">
                                                                Custom quote
                                                              </p> : <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-1707">
                                                                $
                                                                {hasBulkDiscount ? Math.ceil(effectivePrice * (1 - bulkDiscountPct)) : effectivePrice}{' '}
                                                                <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-1708">
                                                                  / yard
                                                                </span>
                                                              </p>}
                                                            {/* Small-load subtext — shows the
                                                             base rate the customer can drop to
                                                             by ordering more. */}
                                                            {!isCustomQuoteZone && !hasBulkDiscount && effectivePrice > product.pricePerYard && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5 whitespace-nowrap" data-id="element-1709">
                                                                  $
                                                                  {product.pricePerYard}
                                                                  /yd at{' '}
                                                                  {smallLoadThreshold}{' '}
                                                                  yd+
                                                                </p>}
                                                            {/* Bulk-discount savings line —
                                                             accent green to match the green
                                                             pill above. */}
                                                            {hasBulkDiscount && !isCustomQuoteZone && <p className="text-sm mt-1" data-id="element-1710">
                                                                  <span className="line-through text-stone-500 dark:text-stone-400" data-id="element-1711">
                                                                    $
                                                                    {product.pricePerYard}
                                                                    /yd
                                                                  </span>
                                                                  <span className="text-[#059F40] ml-2 font-semibold" data-id="element-1712">
                                                                    $
                                                                    {(preDiscountTotal - total).toLocaleString()}{' '}
                                                                    savings
                                                                  </span>
                                                                </p>}
                                                          </div>
                                                          <div className="flex-1 min-w-0 text-right" data-id="element-1713">
                                                            {/* Invisible spacer matching the
                                                             left-column label container so
                                                             both big numbers land on the same
                                                             baseline. */}
                                                            <div aria-hidden="true" className="h-6 mb-1 select-none" data-id="element-1714" />
                                                            {isCustomQuoteZone ? <>
                                                                <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-1715">
                                                                  <span className="text-sm font-normal text-brand-muted dark:text-stone-400" data-id="element-1716">
                                                                    {qtyNum} yds
                                                                    /
                                                                  </span>{' '}
                                                                  Custom
                                                                </p>
                                                                <p className="text-xs text-brand-orange mt-0.5" data-id="element-1717">
                                                                  text us for
                                                                  pricing
                                                                </p>
                                                              </> : <>
                                                                <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-1718">
                                                                  <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-1719">
                                                                    {qtyNum} yds
                                                                    /
                                                                  </span>{' '}
                                                                  ${total}
                                                                </p>
                                                                <p className="text-xs text-brand-muted dark:text-[#22c95c] mt-0.5" data-id="element-1720">
                                                                  + delivery
                                                                </p>
                                                              </>}
                                                          </div>
                                                        </div>

                                                        {hasMessages && <div className="mt-4 pt-3 border-t border-brand-charcoal/5 dark:border-white/5 space-y-1.5" data-id="element-1721">
                                                            {/* Green "X% bulk discount applied"
                                                             headline removed — duplicated the
                                                             yellow "X% BULK DISCOUNT" pill in
                                                             the Price block above. The
                                                             strikethrough "Was $X" went with
                                                             it; the existing amber "Save $X on
                                                             Y yards" copy already conveys the
                                                             savings story without echoing
                                                             the same percentage. */}
                                                            {!isBeyondFullLoad && isBelowMinCharge && <p className="text-xs text-brand-orange" data-id="element-1722">
                                                                  {minChargeYards}{' '}
                                                                  yard minimum
                                                                  charge applies
                                                                </p>}
                                                            {/* Materials breakdown — ALWAYS visible
                                                             when there's a quantity. Surfaces the
                                                             per-yard rate in plain language: single
                                                             small load → 1 "Small-load rate" line;
                                                             single full load or all-full multi-load
                                                             → 1 "Full-load rate" (or bulk-discount)
                                                             line; full + partial mix → 2 lines so
                                                             the customer sees exactly which yards
                                                             run at each rate. */}
                                                            {qtyNum > 0 && !isCustomQuoteZone && (() => {
                                            const _fullLoadCount = Math.floor(qtyNum / fullLoadYards);
                                            const _fullLoadYards = _fullLoadCount * fullLoadYards;
                                            const _partialYards = qtyNum - _fullLoadYards;
                                            const _partialSurcharge = _partialYards > 0 ? getSmallLoadSurcharge(_partialYards, product.name) : 1;
                                            const fullLoadPerYd = Math.ceil(product.pricePerYard * (1 - bulkDiscountPct));
                                            const partialPerYd = _partialYards > 0 ? Math.ceil(product.pricePerYard * _partialSurcharge * (1 - bulkDiscountPct)) : 0;
                                            const fullLoadSubtotal = _fullLoadYards * fullLoadPerYd;
                                            const partialSubtotal = _partialYards * partialPerYd;
                                            const hasMixedBreakdown = _fullLoadCount > 0 && _partialYards > 0;
                                            const fullLoadTag = bulkDiscountPct > 0 ? <span className="text-[#059F40] font-semibold" data-id="element-1723">
                                                                      {Math.round(bulkDiscountPct * 100)}
                                                                      % bulk
                                                                      discount
                                                                    </span> : <span className="text-brand-charcoal/50 dark:text-white/50 font-medium" data-id="element-1724">
                                                                      Full-load
                                                                      rate
                                                                    </span>;
                                            return <div className="space-y-1.5" data-id="element-1725">
                                                                    <div className="text-xs uppercase tracking-wider font-bold text-brand-charcoal/60 dark:text-white/60 mb-1" data-id="element-1726">
                                                                      Materials
                                                                    </div>
                                                                    {hasMixedBreakdown ? <>
                                                                        <div className="flex items-baseline justify-between gap-3" data-id="element-1727">
                                                                          <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-1728">
                                                                            <span data-id="element-1729">
                                                                              $
                                                                              {fullLoadPerYd}
                                                                              /yd
                                                                              ×{' '}
                                                                              {_fullLoadYards}{' '}
                                                                              yd
                                                                            </span>
                                                                            {fullLoadTag}
                                                                          </div>
                                                                          <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-1730">
                                                                            $
                                                                            {fullLoadSubtotal.toLocaleString()}
                                                                          </div>
                                                                        </div>
                                                                        <div className="flex items-baseline justify-between gap-3" data-id="element-1731">
                                                                          <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-1732">
                                                                            <span data-id="element-1733">
                                                                              $
                                                                              {partialPerYd}
                                                                              /yd
                                                                              ×{' '}
                                                                              {_partialYards}{' '}
                                                                              yd
                                                                            </span>
                                                                            {_partialSurcharge > 1 ? <span className="text-[#EAB308] font-semibold" data-id="element-1734">
                                                                                Small-load
                                                                                rate
                                                                              </span> : fullLoadTag}
                                                                          </div>
                                                                          <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-1735">
                                                                            $
                                                                            {partialSubtotal.toLocaleString()}
                                                                          </div>
                                                                        </div>
                                                                      </> : _partialYards > 0 && _partialSurcharge > 1 ? (
                                              /* Single small-load order BELOW threshold —
                                              surcharge in effect, tag "Small-load rate". */
                                              <div className="flex items-baseline justify-between gap-3" data-id="element-1736">
                                                                        <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-1737">
                                                                          <span data-id="element-1738">
                                                                            $
                                                                            {partialPerYd}
                                                                            /yd
                                                                            ×{' '}
                                                                            {_partialYards}{' '}
                                                                            yd
                                                                          </span>
                                                                          <span className="text-[#EAB308] font-semibold" data-id="element-1739">
                                                                            Small-load
                                                                            rate
                                                                          </span>
                                                                        </div>
                                                                        <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-1740">
                                                                          $
                                                                          {partialSubtotal.toLocaleString()}
                                                                        </div>
                                                                      </div>) : _partialYards > 0 ? (
                                              /* At-or-above-threshold but below one full
                                              load (e.g. 10–11 yd heavy) — surcharge = 1,
                                              render with the full-load tag. */
                                              <div className="flex items-baseline justify-between gap-3" data-id="element-1741">
                                                                        <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-1742">
                                                                          <span data-id="element-1743">
                                                                            $
                                                                            {partialPerYd}
                                                                            /yd
                                                                            ×{' '}
                                                                            {_partialYards}{' '}
                                                                            yd
                                                                          </span>
                                                                          {fullLoadTag}
                                                                        </div>
                                                                        <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-1744">
                                                                          $
                                                                          {partialSubtotal.toLocaleString()}
                                                                        </div>
                                                                      </div>) : <div className="flex items-baseline justify-between gap-3" data-id="element-1745">
                                                                        <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-1746">
                                                                          <span data-id="element-1747">
                                                                            $
                                                                            {fullLoadPerYd}
                                                                            /yd
                                                                            ×{' '}
                                                                            {_fullLoadYards}{' '}
                                                                            yd
                                                                          </span>
                                                                          {fullLoadTag}
                                                                        </div>
                                                                        <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-1748">
                                                                          $
                                                                          {fullLoadSubtotal.toLocaleString()}
                                                                        </div>
                                                                      </div>}
                                                                    {deliveryLoads.length > 1 && <p className="text-xs text-brand-orange mt-1" data-id="element-1749">
                                                                        +
                                                                        additional
                                                                        delivery
                                                                        fee for{' '}
                                                                        {deliveryLoads.length - 1}{' '}
                                                                        extra{' '}
                                                                        {deliveryLoads.length > 2 ? 'loads' : 'load'}
                                                                      </p>}
                                                                  </div>;
                                          })()}
                                                          </div>}
                                                      </div>;
                                    })()}
                                                </div>

                                                <div className="space-y-4 mt-auto" data-id="element-1750">
                                                  {addedFlash === product.name ? <motion.div initial={{
                                      opacity: 0,
                                      y: 10
                                    }} animate={{
                                      opacity: 1,
                                      y: 0
                                    }} className="space-y-3" data-id="element-1751">
                                                      <div className="flex items-center justify-center w-full py-3 bg-[#059F40]/10 dark:bg-[#006241]/40 text-[#059F40] dark:text-[#22c95c] font-bold text-sm border border-[#059F40] dark:border-[#22c95c] rounded-none" data-id="element-1752">
                                                        <Check className="w-4 h-4 mr-2" data-id="element-1753" />
                                                        Added to Truck!
                                                      </div>
                                                      <div className="flex gap-2" data-id="element-1754">
                                                        <button onClick={() => {
                                          setAddedFlash(null);
                                          setExpandedCategory(null);
                                          setCategoryOverview(null);
                                          setTimeout(() => {
                                            const el = document.getElementById('materials');
                                            if (el) {
                                              const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                              const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
                                              window.scrollTo({
                                                top,
                                                behavior: 'smooth'
                                              });
                                            }
                                          }, 100);
                                        }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-brand-charcoal dark:text-white border border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-1755">
                                                          Continue Shopping
                                                        </button>
                                                        <button onClick={() => {
                                          setAddedFlash(null);
                                          openCart();
                                        }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-orange hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-1.5" data-id="element-1756">
                                                          <ShoppingCart className="w-4 h-4" data-id="element-1757" />
                                                          View Cart
                                                        </button>
                                                      </div>
                                                    </motion.div> : <>
                                                      <div data-id="element-1758">
                                                        <div className="flex items-center justify-between gap-3 flex-wrap mb-2" data-id="element-1759">
                                                          <span className="block text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-1760">
                                                            Select Quantity
                                                          </span>
                                                          {(() => {
                                            const isLight = isLightMaterial(product.name);
                                            const fullLoad = isLight ? 15 : 12;
                                            const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                                            const tier3Yards = fullLoad * 4;
                                            return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-1761">
                                                                5% at{' '}
                                                                {tier2Yards} yd
                                                                · 10% at{' '}
                                                                {tier3Yards} yd
                                                              </span>;
                                          })()}
                                                        </div>

                                                        <div className="space-y-3" data-id="element-1762">
                                                          {/* Floating stepper — no container background, big
                                                           bold + and − pads, instant green flash on press.
                                                           Below full load: +1 yd. At/above: snap by full
                                                           load (2, 3, 4 loads). + at 4 loads flips to amber
                                                           5+ warning link; − clears the warning. */}
                                                          <div className="flex items-center justify-between gap-3" data-id="element-1763">
                                                            <button type="button" onClick={() => {
                                              const isLight = isLightMaterial(product.name);
                                              const fullLoad = isLight ? 15 : 12;
                                              const MIN = 3;
                                              if (selectedQuantity <= fullLoad) {
                                                if (selectedQuantity > MIN) {
                                                  setSelectedQuantity(selectedQuantity - 1);
                                                }
                                              } else {
                                                const currentLoads = Math.round(selectedQuantity / fullLoad);
                                                const newLoads = currentLoads - 1;
                                                setSelectedQuantity(newLoads * fullLoad);
                                                if (newLoads <= 4) setShowQuoteWarning(false);
                                              }
                                            }} aria-label="Decrease quantity" className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 flex items-center justify-center rounded-xl border border-stone-200 dark:border-white/15 text-brand-charcoal dark:text-white hover:bg-stone-100 dark:hover:bg-white/5 active:bg-[#059F40]/20 active:border-[#059F40] active:text-[#059F40] dark:active:bg-[#22c95c]/15 dark:active:border-[#22c95c] dark:active:text-[#22c95c] transition-colors duration-75" data-id="element-1764">
                                                              <Minus className="w-10 h-10" strokeWidth={2.5} data-id="element-1765" />
                                                            </button>
                                                            <div className="flex-1 text-center text-2xl font-bold text-brand-charcoal dark:text-white tabular-nums leading-tight" data-id="element-1766">
                                                              {(() => {
                                                const isLight = isLightMaterial(product.name);
                                                const fullLoad = isLight ? 15 : 12;
                                                const loadCount = selectedQuantity > fullLoad ? Math.round(selectedQuantity / fullLoad) : 1;
                                                const isFullLoad = selectedQuantity === fullLoad;
                                                const isMin = selectedQuantity === 3;
                                                const commit = () => {
                                                  const parsed = parseInt(qtyDraft, 10);
                                                  if (isNaN(parsed) || parsed <= 0) {
                                                    setQtyDraft(String(selectedQuantity));
                                                    return;
                                                  }
                                                  const max = fullLoad * 8;
                                                  const clamped = Math.min(max, Math.max(1, parsed));
                                                  setSelectedQuantity(clamped);
                                                  setQtyDraft(String(clamped));
                                                  if (clamped > fullLoad * 4) {
                                                    setShowQuoteWarning(true);
                                                  } else {
                                                    setShowQuoteWarning(false);
                                                  }
                                                };
                                                return <>
                                                                    <input ref={qtyInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={qtyDraft} maxLength={2} onFocus={e => e.currentTarget.select()} onChange={e => {
                                                    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                                                    setQtyDraft(v);
                                                  }} onBlur={commit} onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                      e.preventDefault();
                                                      (e.currentTarget as HTMLInputElement).blur();
                                                    } else if (e.key === 'Escape') {
                                                      e.preventDefault();
                                                      setQtyDraft(String(selectedQuantity));
                                                      (e.currentTarget as HTMLInputElement).blur();
                                                    }
                                                  }} aria-label="Quantity in yards" style={{
                                                    width: `${Math.max(1, qtyDraft.length)}ch`
                                                  }} className={`bg-transparent border-0 outline-none text-center text-2xl font-bold tabular-nums p-0 m-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${isMin ? 'text-[#EAB308]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-1767" />
                                                                    <span className={`hidden sm:inline transition-colors ${isMin ? 'text-[#EAB308]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-1768">
                                                                      {' '}
                                                                      yards
                                                                    </span>
                                                                    <span className={`sm:hidden transition-colors ${isMin ? 'text-[#EAB308]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-1769">
                                                                      {' '}
                                                                      yd
                                                                    </span>
                                                                    <span className="ml-2 text-[11px] font-normal text-brand-muted dark:text-stone-400 align-baseline" data-id="element-1770">
                                                                      {loadCount}{' '}
                                                                      {loadCount === 1 ? 'load' : 'loads'}
                                                                    </span>
                                                                  </>;
                                              })()}
                                                            </div>
                                                            <button type="button" onClick={() => {
                                              const isLight = isLightMaterial(product.name);
                                              const fullLoad = isLight ? 15 : 12;
                                              if (selectedQuantity < fullLoad) {
                                                setSelectedQuantity(selectedQuantity + 1);
                                              } else {
                                                const currentLoads = Math.round(selectedQuantity / fullLoad);
                                                // Cap at 8 loads — past 4 loads
                                                // the primary CTA flips to amber
                                                // "text us" but the stepper still
                                                // works so customers can show the
                                                // exact yardage they need.
                                                if (currentLoads >= 8) return;
                                                const newLoads = currentLoads + 1;
                                                setSelectedQuantity(newLoads * fullLoad);
                                                if (newLoads >= 5) setShowQuoteWarning(true);
                                              }
                                            }} aria-label="Increase quantity" className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 flex items-center justify-center rounded-xl border border-stone-200 dark:border-white/15 text-brand-charcoal dark:text-white hover:bg-stone-100 dark:hover:bg-white/5 active:bg-[#059F40]/20 active:border-[#059F40] active:text-[#059F40] dark:active:bg-[#22c95c]/15 dark:active:border-[#22c95c] dark:active:text-[#22c95c] transition-colors duration-75" data-id="element-1771">
                                                              <Plus className="w-10 h-10" strokeWidth={2.5} data-id="element-1772" />
                                                            </button>
                                                          </div>

                                                          {/* Large-order handoff — the primary CTA
                                                           below morphs into an amber "Text us for a
                                                           quote" button when + has pushed past 4
                                                           loads. SMS hand-off on mobile, QuoteModal
                                                           fallback on desktop. */}

                                                          {showQuoteWarning ? <button onClick={() => {
                                            const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                                            if (!isMobile) {
                                              setIsQuoteModalOpen(true);
                                              return;
                                            }
                                            const isLight = isLightMaterial(product.name);
                                            const fullLoad = isLight ? 15 : 12;
                                            const yds = selectedQuantity || fullLoad * 5;
                                            const loads = Math.max(5, Math.round(yds / fullLoad));
                                            const body = `Hi 253Gravel — I need a custom quote for ${loads} loads of ${product.name} (${yds} yd). Thanks!`;
                                            window.location.href = `sms:+12066193742?&body=${encodeURIComponent(body)}`;
                                          }} className="w-full py-[18px] rounded-none font-bold text-lg transition-all duration-300 flex items-center justify-center bg-amber-500/15 dark:bg-amber-500/15 hover:bg-amber-500/25 dark:hover:bg-amber-500/25 text-amber-700 dark:text-amber-300 border-2 border-amber-500/40 hover:border-amber-500/60" aria-live="polite" data-id="element-1773">
                                                              <MessageSquare className="w-5 h-5 mr-2" data-id="element-1774" />
                                                              Large order — text
                                                              us →
                                                            </button> : (() => {
                                            const isLight = isLightMaterial(product.name);
                                            const fullLoad = isLight ? 15 : 12;
                                            const loadPct = Math.min(100, Math.max(0, selectedQuantity / fullLoad * 100));
                                            return <button onClick={() => handleConfirmAdd(category.name, product.name, product.pricePerYard)} className="relative w-full overflow-hidden bg-[#04702E] text-white py-[18px] rounded-none font-bold text-lg flex items-center justify-center shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-1775">
                                                                  <motion.span aria-hidden="true" className="absolute inset-y-0 left-0 bg-[#059F40]" initial={false} animate={{
                                                width: `${loadPct}%`
                                              }} transition={{
                                                type: 'spring',
                                                stiffness: 260,
                                                damping: 26
                                              }} data-id="element-1776" />
                                                                  <span className="relative z-10 flex items-center justify-center" data-id="element-1777">
                                                                    <Truck className="w-5 h-5 mr-2" data-id="element-1778" />
                                                                    Add to Truck
                                                                  </span>
                                                                </button>;
                                          })()}
                                                        </div>
                                                        <div className="mt-3 flex justify-center" data-id="element-1779">
                                                          <SameDayBadge variant="full" data-id="element-1780" />
                                                        </div>
                                                        {/* SmallLoadBreakdown moved INTO the price
                                                         box above (where the inline green upcharge
                                                         line used to live), so the cost narrative
                                                         is in one place instead of split across
                                                         two sections. */}
                                                        {/* Material sanity check — same component used on the
                                                         dedicated product page. Lives here so customers
                                                         browsing the materials hub get the "is this the
                                                         right pick?" check BEFORE adding to cart. */}
                                                        <div className="mt-3" data-id="element-1781">
                                                          <CartHelper contextProduct={{
                                            slug: product.slug,
                                            name: product.name
                                          }} data-id="element-1782" />
                                                        </div>
                                                      </div>

                                                      {/* Inline Calculator */}
                                                      <div className="mt-4" data-id="element-1783">
                                                        <button onClick={() => setShowInlineCalc(!showInlineCalc)} className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-1784">
                                                          <Calculator className="w-4 h-4" data-id="element-1785" />
                                                          {showInlineCalc ? 'Hide Calculator' : 'Calculate how much I need'}
                                                        </button>

                                                        <AnimatePresence data-id="element-1786">
                                                          {showInlineCalc && <motion.div initial={{
                                            opacity: 0,
                                            height: 0
                                          }} animate={{
                                            opacity: 1,
                                            height: 'auto'
                                          }} exit={{
                                            opacity: 0,
                                            height: 0
                                          }} className="overflow-hidden" data-id="element-1787">
                                                              <div className="p-4 mt-2 bg-stone-50 dark:bg-[#303030] border border-stone-200 dark:border-[#4b4b4b] rounded-xl space-y-4" data-id="element-1788">
                                                                {/* Shape Selector */}
                                                                <div data-id="element-1789">
                                                                  <label className="block text-[11px] font-bold text-stone-600 dark:text-stone-300 uppercase tracking-wider mb-2" data-id="element-1790">
                                                                    Area Shape
                                                                  </label>
                                                                  <div className="grid grid-cols-4 gap-1.5" data-id="element-1791">
                                                                    {([{
                                                    id: 'rectangle',
                                                    label: 'Rect'
                                                  }, {
                                                    id: 'circle',
                                                    label: 'Circle'
                                                  }, {
                                                    id: 'triangle',
                                                    label: 'Tri'
                                                  }, {
                                                    id: 'irregular',
                                                    label: 'Other'
                                                  }] as const).map(s => <button key={s.id} onClick={() => setCalcShape(s.id)} className={`py-2 rounded-lg text-xs font-bold transition-colors ${calcShape === s.id ? 'bg-brand-orange text-white' : 'bg-white dark:bg-[#252525] text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-[#2f2f2f] border border-stone-200 dark:border-[#404040]'}`} data-id="element-1792">
                                                                          {s.label}
                                                                        </button>)}
                                                                  </div>
                                                                </div>

                                                                {/* Unit Toggles */}
                                                                <div className="flex items-center gap-3 text-[11px]" data-id="element-1793">
                                                                  <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1794">
                                                                    Units:
                                                                  </span>
                                                                  <div className="inline-flex rounded-md border border-stone-200 dark:border-[#404040] overflow-hidden" data-id="element-1795">
                                                                    {(['ft', 'm'] as const).map(u => <button key={u} onClick={() => setCalcUnit(u)} className={`px-2.5 py-1 font-bold transition-colors ${calcUnit === u ? 'bg-brand-orange text-white' : 'bg-white dark:bg-[#252525] text-stone-600 dark:text-stone-400'}`} data-id="element-1796">
                                                                          {u}
                                                                        </button>)}
                                                                  </div>
                                                                  <span className="font-medium text-stone-500 dark:text-stone-400 ml-2" data-id="element-1797">
                                                                    Depth:
                                                                  </span>
                                                                  <div className="inline-flex rounded-md border border-stone-200 dark:border-[#404040] overflow-hidden" data-id="element-1798">
                                                                    {(['in', 'cm'] as const).map(u => <button key={u} onClick={() => setCalcDepthUnit(u)} className={`px-2.5 py-1 font-bold transition-colors ${calcDepthUnit === u ? 'bg-brand-orange text-white' : 'bg-white dark:bg-[#252525] text-stone-600 dark:text-stone-400'}`} data-id="element-1799">
                                                                          {u}
                                                                        </button>)}
                                                                  </div>
                                                                </div>

                                                                {/* Dimension Inputs (per shape) */}
                                                                {calcShape === 'rectangle' || calcShape === 'irregular' ? <div className="grid grid-cols-3 gap-3" data-id="element-1800">
                                                                    <div data-id="element-1801">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1802">
                                                                        Length (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcLength} onChange={e => setInlineCalcLength(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1803" />
                                                                    </div>
                                                                    <div data-id="element-1804">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1805">
                                                                        Width (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcWidth} onChange={e => setInlineCalcWidth(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1806" />
                                                                    </div>
                                                                    <div data-id="element-1807">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1808">
                                                                        Depth (
                                                                        {calcDepthUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcDepth} onChange={e => setInlineCalcDepth(e.target.value)} placeholder="3" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1809" />
                                                                    </div>
                                                                  </div> : calcShape === 'circle' ? <div className="grid grid-cols-2 gap-3" data-id="element-1810">
                                                                    <div data-id="element-1811">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1812">
                                                                        Diameter
                                                                        (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcDiameter} onChange={e => setInlineCalcDiameter(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1813" />
                                                                    </div>
                                                                    <div data-id="element-1814">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1815">
                                                                        Depth (
                                                                        {calcDepthUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcDepth} onChange={e => setInlineCalcDepth(e.target.value)} placeholder="3" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1816" />
                                                                    </div>
                                                                  </div> : <div className="grid grid-cols-3 gap-3" data-id="element-1817">
                                                                    <div data-id="element-1818">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1819">
                                                                        Base (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcBase} onChange={e => setInlineCalcBase(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1820" />
                                                                    </div>
                                                                    <div data-id="element-1821">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1822">
                                                                        Height (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcHeight} onChange={e => setInlineCalcHeight(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1823" />
                                                                    </div>
                                                                    <div data-id="element-1824">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1825">
                                                                        Depth (
                                                                        {calcDepthUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcDepth} onChange={e => setInlineCalcDepth(e.target.value)} placeholder="3" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1826" />
                                                                    </div>
                                                                  </div>}

                                                                {(() => {
                                                // Convert all dimensions to feet
                                                const mToFt = 3.28084;
                                                const toFt = (v: number) => calcUnit === 'm' ? v * mToFt : v;
                                                const l = toFt(parseFloat(inlineCalcLength) || 0);
                                                const w = toFt(parseFloat(inlineCalcWidth) || 0);
                                                const dia = toFt(parseFloat(inlineCalcDiameter) || 0);
                                                const b = toFt(parseFloat(inlineCalcBase) || 0);
                                                const h = toFt(parseFloat(inlineCalcHeight) || 0);
                                                const dpRaw = parseFloat(inlineCalcDepth) || 0;
                                                const dpFt = calcDepthUnit === 'cm' ? dpRaw / 30.48 : dpRaw / 12;
                                                let areaSqFt = 0;
                                                if (calcShape === 'rectangle' || calcShape === 'irregular') {
                                                  areaSqFt = l * w;
                                                } else if (calcShape === 'circle') {
                                                  const r = dia / 2;
                                                  areaSqFt = Math.PI * r * r;
                                                } else {
                                                  areaSqFt = 0.5 * b * h;
                                                }
                                                const cubicYards = areaSqFt * dpFt / 27;
                                                const density = getDensity(product.name);
                                                const tons = cubicYards * density;
                                                const roundedYards = Math.max(1, Math.ceil(cubicYards));
                                                const hasInput = areaSqFt > 0 && dpFt > 0;
                                                if (!hasInput) return null;
                                                return <div className="pt-3 border-t border-stone-200 dark:border-[#404040] space-y-3" data-id="element-1827">
                                                                      <div className="grid grid-cols-2 gap-3" data-id="element-1828">
                                                                        <div className="bg-white dark:bg-[#252525] rounded-lg p-3 border border-stone-200 dark:border-[#404040]" data-id="element-1829">
                                                                          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1830">
                                                                            Cubic
                                                                            Yards
                                                                          </div>
                                                                          <div className="text-2xl font-bold text-stone-900 dark:text-white leading-none" data-id="element-1831">
                                                                            {cubicYards.toFixed(1)}
                                                                          </div>
                                                                        </div>
                                                                        <div className="bg-white dark:bg-[#252525] rounded-lg p-3 border border-stone-200 dark:border-[#404040]" data-id="element-1832">
                                                                          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1833">
                                                                            Est.
                                                                            Tons
                                                                          </div>
                                                                          <div className="text-2xl font-bold text-stone-900 dark:text-white leading-none" data-id="element-1834">
                                                                            {tons.toFixed(1)}
                                                                          </div>
                                                                        </div>
                                                                      </div>
                                                                      <button onClick={() => {
                                                    const isLight = isLightMaterial(product.name);
                                                    const maxQty = isLight ? 60 : 48;
                                                    const minQty = 3;
                                                    const snapped = Math.min(maxQty, Math.max(minQty, roundedYards));
                                                    setSelectedQuantity(snapped);
                                                    setShowInlineCalc(false);
                                                  }} className="w-full py-3 bg-brand-button hover:bg-brand-buttonHover text-white text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-1835">
                                                                        <Check className="w-4 h-4" data-id="element-1836" />
                                                                        Use{' '}
                                                                        {roundedYards}{' '}
                                                                        Yards
                                                                      </button>
                                                                      <p className="text-[10px] text-stone-500 dark:text-stone-400 text-center leading-relaxed" data-id="element-1837">
                                                                        Tons
                                                                        estimated
                                                                        using
                                                                        density
                                                                        for{' '}
                                                                        <span className="font-semibold text-stone-700 dark:text-stone-300" data-id="element-1838">
                                                                          {product.name}
                                                                        </span>
                                                                        . Add
                                                                        ~10% for
                                                                        compaction
                                                                        & waste.
                                                                      </p>
                                                                    </div>;
                                              })()}
                                                              </div>
                                                            </motion.div>}
                                                        </AnimatePresence>
                                                      </div>

                                                      {/* "Not sure? Take the quiz" link removed — the inline
                                                       CartHelper above already handles "is this right?"
                                                       in two taps without sending the customer to
                                                       another page. Compare materials stays. */}
                                                      <div className="mt-4 pt-3 border-t border-stone-200 dark:border-[#3a3a3a] flex items-center justify-center text-xs" data-id="element-1839">
                                                        <Link to={`/compare?add=${encodeURIComponent(product.slug)}`} className="flex items-center gap-1.5 text-stone-600 dark:text-white/60 hover:text-brand-orange dark:hover:text-brand-orange transition-colors font-medium" data-id="element-1840">
                                                          <RefreshCw className="w-3.5 h-3.5" data-id="element-1841" />
                                                          <span data-id="element-1842">
                                                            Compare materials
                                                          </span>
                                                        </Link>
                                                      </div>
                                                    </>}
                                                </div>
                                              </> : <div className="mt-auto space-y-4" data-id="element-1843">
                                                <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-3.5 rounded-xl font-bold transition-colors flex items-center justify-center" data-id="element-1844">
                                                  <Phone className="w-5 h-5 mr-2" data-id="element-1845" />
                                                  Call for Pricing
                                                </a>
                                                <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-3.5 rounded-xl font-bold transition-colors" data-id="element-1846">
                                                  Get a Custom Quote
                                                </button>
                                              </div>}
                                          </div>
                                        </div>}
                                    </>}
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
                }
                return <div key={category.name} id={`cat-${category.name}`} className="overflow-hidden scroll-mt-[135px]" data-id="element-1847">
                        <button onClick={() => {
                    setExpandedCategory(category.name);
                    setCategoryOverview(category.name);
                    setCarouselIndex(0);
                    setTimeout(() => {
                      document.getElementById(`cat-${category.name}`)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 100);
                  }} className="w-full flex items-center justify-between py-3 sm:py-4 text-left focus:outline-none group" data-id="element-1848">
                          <div data-id="element-1849">
                            <h3 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors duration-200 text-brand-charcoal dark:text-white/80 group-hover:text-brand-orange`} data-id="element-1850">
                              {category.name}
                            </h3>
                            <span className="text-[11px] font-normal text-stone-400 dark:text-white/40 tracking-wide" data-id="element-1851">
                              {category.name === 'Soil, Mulch & Org.' ? '15' : '12'}{' '}
                              yards max load
                            </span>
                          </div>
                          <ChevronDown className="w-7 h-7 shrink-0 ml-4 transition-transform duration-300 text-brand-charcoal/30 dark:text-white/30 group-hover:text-brand-orange" data-id="element-1852" />
                        </button>
                      </div>;
              })}

                  {/* View Full Catalog Button & Expanded Grid */}
                  <div className="pt-6" data-id="element-1853">
                    <button onClick={() => {
                  const wasOpen = showAllCatalog;
                  setShowAllCatalog(!showAllCatalog);
                  if (wasOpen) {
                    setTimeout(() => {
                      document.getElementById('materials')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 450);
                  }
                }} className="mx-auto flex items-center gap-2 py-2 px-4 text-stone-800 dark:text-stone-300 hover:text-brand-orange transition-colors text-xs uppercase tracking-wider" data-id="element-1854">
                      <motion.span animate={{
                    rotate: showAllCatalog ? 45 : 0
                  }} transition={{
                    duration: 0.2
                  }} className="w-5 h-5 flex items-center justify-center" data-id="element-1855">
                        <span className="text-sm font-bold leading-none" data-id="element-1856">
                          +
                        </span>
                      </motion.span>
                      <span className="font-bold text-sm uppercase tracking-wider" data-id="element-1857">
                        {showAllCatalog ? 'Collapse Catalog' : 'View Full Catalog'}
                      </span>
                    </button>

                    <AnimatePresence data-id="element-1858">
                      {showAllCatalog && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.4,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-1859">
                          <div className="pt-8 space-y-12" data-id="element-1860">
                            {categories.map(category => <div key={`catalog-${category.name}`} data-id="element-1861">
                                <h3 className="font-heading text-2xl font-bold text-brand-charcoal dark:text-white mb-5 flex items-center" data-id="element-1862">
                                  <span className="w-2 h-7 bg-brand-orange mr-3 rounded-sm" data-id="element-1863" />
                                  {category.name}
                                </h3>
                                <div className="space-y-1.5" data-id="element-1864">
                                  {category.products.map(product => <button key={product.slug} onClick={() => {
                            setShowAllCatalog(false);
                            setExpandedCategory(category.name);
                            setCategoryOverview(null);
                            setCarouselIndex(category.products.findIndex(p => p.slug === product.slug));
                            setTimeout(() => {
                              document.getElementById(`cat-${category.name}`)?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                              });
                            }, 100);
                          }} className="w-full relative overflow-hidden rounded-xl group/card text-left block" style={{
                            height: '200px'
                          }} data-id="element-1865">
                                      <img src={product.images?.[0] || product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" data-id="element-1866" />
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-1867" />
                                      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6" data-id="element-1868">
                                        <h4 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight mb-1" data-id="element-1869">
                                          {product.name}
                                        </h4>
                                        <p className="text-white/90 text-sm sm:text-base font-medium" data-id="element-1870">
                                          ${product.pricePerYard} / yard
                                        </p>
                                      </div>
                                    </button>)}
                                </div>
                              </div>)}
                          </div>
                          <button onClick={() => {
                      setShowAllCatalog(false);
                      setTimeout(() => {
                        document.getElementById('materials')?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }, 450);
                    }} className="mx-auto flex items-center gap-2 mt-10 py-3 px-6 text-stone-500 dark:text-stone-400 hover:text-brand-orange transition-colors text-sm font-bold uppercase tracking-wider border border-stone-200 dark:border-[#404040] hover:border-brand-orange/50 rounded-lg" data-id="element-1871">
                            <ChevronDown className="w-4 h-4 rotate-180" data-id="element-1872" />
                            Collapse Catalog
                          </button>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>}
              {/* CALCULATOR TAB */}
              {activeTab === 'calculator' && <motion.div key="calculator" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1873">
                  <MaterialCalculator variant="inline" initialMaterial={calcMaterial} data-id="element-1874" />
                </motion.div>}

              {/* QUIZ TAB */}
              {activeTab === 'quiz' && <motion.div key="quiz" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="max-w-2xl mx-auto" data-id="element-1875">
                  <div className="bg-[#fdfbf7] dark:bg-[#232221] rounded-2xl border border-stone-200 dark:border-[#333231] shadow-sm p-8 text-center transition-colors duration-300" data-id="element-1876">
                    {quizResult ? <motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} data-id="element-1877">
                        <div className="w-16 h-16 bg-brand-orange/10 dark:bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1878">
                          <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1879" />
                        </div>
                        <h3 className="text-xl text-brand-muted dark:text-stone-200 mb-2" data-id="element-1880">
                          We recommend:
                        </h3>
                        <h2 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1881">
                          {quizResult.name}
                        </h2>
                        <p className="text-brand-muted dark:text-stone-200 mb-8 max-w-md mx-auto" data-id="element-1882">
                          {quizResult.desc}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1883">
                          <button onClick={handleCalculateFromQuiz} className="inline-flex items-center justify-center px-6 py-2.5 bg-brand-orange text-white rounded-lg font-semibold text-sm hover:bg-brand-orange/90 transition-colors group" data-id="element-1884">
                            <Calculator className="w-4 h-4 mr-2" data-id="element-1885" />
                            Calculate How Much I Need
                            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-1886" />
                          </button>
                          <button onClick={() => setActiveTab('materials')} className="inline-flex items-center justify-center px-6 py-2.5 bg-stone-50 dark:bg-[#3a3a3a] border border-stone-200 dark:border-[#555555] text-brand-charcoal dark:text-white rounded-lg font-medium text-sm hover:bg-stone-100 dark:hover:bg-[#4a4a4a] transition-colors" data-id="element-1887">
                            <ChevronLeft className="w-4 h-4 mr-2" data-id="element-1888" />
                            Back to Materials
                          </button>
                        </div>
                      </motion.div> : currentQuizQuestion ? <motion.div key={currentQuizQuestion.id} initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} data-id="element-1889">
                        <h3 className="font-heading text-2xl font-bold text-brand-charcoal dark:text-white mb-8" data-id="element-1890">
                          {currentQuizQuestion.question}
                        </h3>
                        <div className="space-y-3" data-id="element-1891">
                          {currentQuizQuestion.options.map(option => <button key={option.value} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#333231] bg-white dark:bg-[#1a1918] hover:border-brand-orange dark:hover:border-brand-orange hover:bg-brand-orange/5 dark:hover:bg-brand-orange/10 transition-all group" data-id="element-1892">
                              <div className="flex items-center justify-between" data-id="element-1893">
                                <span className="font-medium text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-1894">
                                  {option.label}
                                </span>
                                <ArrowRight className="w-5 h-5 text-stone-500 dark:text-stone-400 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" data-id="element-1895" />
                              </div>
                            </button>)}
                        </div>
                      </motion.div> : null}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Can't find what you need? */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 bg-transparent border border-stone-200/60 dark:border-[#404040] rounded-2xl px-8 py-6 max-w-7xl mx-auto" data-id="element-1896">
          <div className="text-center md:text-left" data-id="element-1897">
            <p className="text-stone-700 dark:text-stone-300 text-lg font-sub mb-1" data-id="element-1898">
              Can't find what you're looking for?
            </p>
            <p className="text-stone-700 dark:text-stone-300 text-sm" data-id="element-1899">
              We source a wide range of materials beyond what's listed here.
              Tell us what you need and we'll find it for you.
            </p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-full font-bold transition-colors shrink-0" data-id="element-1900">
            Contact Us
            <ArrowRight className="w-4 h-4" data-id="element-1901" />
          </Link>
        </motion.div>
      </section>

      {/* Calculator Modal */}
      <AnimatePresence data-id="element-1902">
        {showCalcModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1903">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCalcModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1904" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-4xl bg-[#474747] border border-[#555555] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1905">
              <div className="flex items-center justify-between p-6 border-b border-[#555555] shrink-0" data-id="element-1906">
                <h3 className="text-xl font-bold text-white flex items-center" data-id="element-1907">
                  <Calculator className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1908" />
                  Material Calculator
                </h3>
                <button onClick={() => setShowCalcModal(false)} className="text-stone-400 hover:text-white transition-colors p-1" data-id="element-1909">
                  <X className="w-6 h-6" data-id="element-1910" />
                </button>
              </div>
              <div className="overflow-y-auto" data-id="element-1911">
                <MaterialCalculator variant="modal" data-id="element-1912" />
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Quiz Modal */}
      <AnimatePresence data-id="element-1913">
        {showQuizModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1914">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowQuizModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1915" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-2xl bg-white dark:bg-[#3d3d3d] border border-stone-200 dark:border-[#4b4b4b] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1916">
              <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-[#4b4b4b] shrink-0" data-id="element-1917">
                <h3 className="text-xl font-bold text-stone-900 dark:text-white flex items-center" data-id="element-1918">
                  <ArrowRight className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1919" />
                  Help Me Choose
                </h3>
                <button onClick={() => setShowQuizModal(false)} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors p-1" data-id="element-1920">
                  <X className="w-6 h-6" data-id="element-1921" />
                </button>
              </div>
              <div className="overflow-y-auto p-8 text-center" data-id="element-1922">
                {quizResult ? <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} data-id="element-1923">
                    <div className="w-16 h-16 bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1924">
                      <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1925" />
                    </div>
                    <h3 className="text-xl text-stone-500 dark:text-stone-400 mb-2" data-id="element-1926">
                      We recommend:
                    </h3>
                    <h2 className="font-heading text-3xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1927">
                      {quizResult.name}
                    </h2>
                    <p className="text-stone-600 dark:text-stone-400 mb-8 max-w-md mx-auto" data-id="element-1928">
                      {quizResult.desc}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1929">
                      <button onClick={() => {
                  setShowQuizModal(false);
                  setIsQuoteModalOpen(true);
                }} className="bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-1930">
                        Get a Quote
                      </button>
                      <button onClick={resetQuiz} className="flex items-center justify-center px-8 py-3 rounded-xl font-bold text-stone-900 dark:text-white bg-stone-100 dark:bg-[#3a3a3a] hover:bg-stone-200 dark:hover:bg-[#4b4b4b] transition-colors" data-id="element-1931">
                        <RefreshCw className="w-4 h-4 mr-2" data-id="element-1932" />
                        Start Over
                      </button>
                    </div>
                  </motion.div> : currentQuizQuestion ? <motion.div key={currentQuizQuestion.id} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-1933">
                    <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-8" data-id="element-1934">
                      {currentQuizQuestion.question}
                    </h3>
                    <div className="space-y-3" data-id="element-1935">
                      {currentQuizQuestion.options.map(option => <button key={option.value} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#4b4b4b] bg-stone-50 dark:bg-[#303030] hover:border-brand-orange hover:bg-brand-orange/10 transition-all group" data-id="element-1936">
                          <div className="flex items-center justify-between" data-id="element-1937">
                            <span className="font-medium text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-1938">
                              {option.label}
                            </span>
                            <ArrowRight className="w-5 h-5 text-stone-300 dark:text-stone-700 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" data-id="element-1939" />
                          </div>
                        </button>)}
                    </div>
                  </motion.div> : null}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1940" />

      {/* Cart Added Toast removed — inline pill handles this now */}

      {/* Swipe Navigation Hint — only on materials product detail view.
                                 Two half-circle bumps slide in from the left/right edges of the
                                 viewport with a single "Swipe to browse" caption between them.
                                 Theme-aware: light surface in light mode, dark surface in dark
                                 mode. Auto-dismisses on first swipe via handleNext/handlePrev,
                                 and tapping anywhere on the overlay also dismisses. */}
      <AnimatePresence data-id="element-1941">
        {showSwipeHint && !isCartOpen && expandedCategory && !categoryOverview && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} onClick={() => {
        setShowSwipeHint(false);
        setSwipeHintDismissed(true);
      }} className="fixed right-0 top-1/2 -translate-y-1/2 z-[100] pointer-events-auto md:hidden cursor-pointer" data-id="element-1942">
              {/* Glass side drawer — slides in from the right edge,
               pinned vertically centered. Frosted backdrop blur reads
               over any underlying product image. Theme-aware: white
               glass surface in light mode, black glass in dark mode.
               Rounded only on the left (visible) edge since the right
               edge sits flush against the viewport. */}
              <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 220,
          damping: 28
        }} className="flex items-center gap-3 pl-5 pr-3 py-4 rounded-l-2xl backdrop-blur-xl bg-white/30 dark:bg-black/30 border border-r-0 border-white/40 dark:border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.25)]" data-id="element-1943">
                <div className="flex flex-col items-start" data-id="element-1944">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-stone-700 dark:text-white/80" data-id="element-1945">
                    Swipe to browse
                  </p>
                  <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-1946">
                    materials
                  </p>
                </div>
                {/* Animated chevron — gently bobs left-to-right to
                 imply the swipe action. */}
                <motion.div animate={{
            x: [0, -6, 0]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-1947">
                  <ChevronLeft className="w-6 h-6 text-stone-900 dark:text-white" strokeWidth={2.5} data-id="element-1948" />
                </motion.div>
              </motion.div>
            </motion.div>}
      </AnimatePresence>
    </>;
}